import {
  approvalLevel,
  formatDate,
  fullName,
  REPORT_STATUSES,
  type ReportStatus,
} from '@hr/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { CheckIcon, ClipboardCheckIcon, FileSpreadsheetIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { DataTable } from '@/components/data-table';
import { DateInput } from '@/components/date-input';
import { EmptyState, PageHeader } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePreview } from '@/features/preview/use-preview';
import { ReportSheet } from '@/features/reports/report-sheet';
import { ReportStatusBadge, ViolationList } from '@/features/reports/report-status';
import { useRegistry } from '@/features/registry/use-registry';
import { REPORT_STATUS_LABELS, uk } from '@/i18n/uk';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { errorMessage, trpc } from '@/lib/trpc';
import type { ServiceReportListItem } from '@/lib/types';

const searchSchema = z.object({
  ...listSearchShape,
  tab: z.enum(['awaiting', 'all']).optional().catch(undefined),
  status: z.enum(REPORT_STATUSES).optional().catch(undefined),
  project: z.uuid().optional().catch(undefined),
  center: z.uuid().optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  violations: z.boolean().optional().catch(undefined),
  report: z.uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/reports')({
  validateSearch: searchSchema,
  component: ReportsPage,
});

function ReportsPage() {
  const openPreview = usePreview();
  const t = uk.reports;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const registry = useRegistry();
  const tab = search.tab ?? 'awaiting';
  const state = tableState(search, { sort: 'date', dir: 'desc', size: 50 });
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const list = useQuery({
    ...trpc.serviceReports.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
      status: tab === 'all' ? search.status : undefined,
      projectId: search.project,
      centerId: search.center,
      from: search.from,
      to: search.to,
      withViolations: search.violations,
      awaitingMe: tab === 'awaiting',
    }),
    placeholderData: keepPreviousData,
  });
  const approve = useMutation(
    trpc.serviceReports.approve.mutationOptions({
      onSuccess: (r) => {
        toast.success(t.approved(r.count));
        setSelected(new Set());
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.serviceReports.pathFilter()),
    }),
  );
  const approvable = (list.data?.items ?? []).filter((r) => r.canApprove);
  const chosen = approvable.filter((r) => selected.has(r.id)).map((r) => r.id);

  const columns: ColumnDef<ServiceReportListItem>[] = [
    {
      id: 'select',
      enableSorting: false,
      header: () => null,
      cell: ({ row }) =>
        row.original.canApprove && (
          <Checkbox
            aria-label={t.approve}
            checked={selected.has(row.original.id)}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(v) =>
              setSelected((prev) => {
                const next = new Set(prev);
                if (v === true) next.add(row.original.id);
                else next.delete(row.original.id);
                return next;
              })
            }
          />
        ),
      meta: { className: 'w-8' },
    },
    {
      id: 'date',
      header: t.date,
      cell: ({ row }) => formatDate(row.original.date),
      meta: { className: 'whitespace-nowrap' },
    },
    {
      id: 'employee',
      header: t.employee,
      cell: ({ row }) => fullName(row.original),
      meta: { className: 'min-w-40' },
    },
    {
      id: 'service',
      header: t.service,
      cell: ({ row }) => (
        <div className="min-w-48">
          <div>{row.original.serviceName}</div>
          <div className="text-xs text-muted-foreground">{row.original.location ?? '—'}</div>
        </div>
      ),
    },
    { id: 'project', header: uk.registry.project, cell: ({ row }) => row.original.projectCode },
    {
      id: 'hours',
      header: t.hours,
      cell: ({ row }) => <span className="tabular-nums">{row.original.hours}</span>,
      meta: { className: 'text-right' },
    },
    {
      id: 'violations',
      header: t.violations,
      enableSorting: false,
      cell: ({ row }) => <ViolationList violations={row.original.violations} />,
    },
    {
      id: 'status',
      header: t.status,
      cell: ({ row }) => {
        const level = approvalLevel(row.original.status);
        return (
          <div className="space-y-0.5">
            <ReportStatusBadge status={row.original.status} />
            {level && (
              <div className="text-xs text-muted-foreground">{t.awaitingLevel(t.level[level])}</div>
            )}
          </div>
        );
      },
    },
  ];

  const exportFrom = search.from ?? new Date().toISOString().slice(0, 8) + '01';
  const exportTo = search.to ?? new Date().toISOString().slice(0, 10);

  return (
    <>
      <PageHeader
        title={t.title}
        actions={
          <Button
            variant="outline"
            onClick={() =>
              openPreview({
                url: `/api/service-reports/export.xlsx?from=${exportFrom}&to=${exportTo}${search.project ? `&projectId=${search.project}` : ''}${search.status ? `&status=${search.status}` : ''}`,
              })
            }
          >
            <FileSpreadsheetIcon /> {t.exportExcel}
          </Button>
        }
      />
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setSelected(new Set());
          setSearch({ tab: v === 'awaiting' ? undefined : 'all', page: undefined });
        }}
        className="mb-3"
      >
        <TabsList>
          <TabsTrigger value="awaiting">{t.awaiting}</TabsTrigger>
          <TabsTrigger value="all">{t.all}</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <SearchInput
          value={search.q ?? ''}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
        {tab === 'all' && (
          <Select
            value={search.status ?? 'all'}
            onValueChange={(v) =>
              setSearch({ status: v === 'all' ? undefined : (v as ReportStatus), page: undefined })
            }
          >
            <SelectTrigger className="w-48" aria-label={t.status}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t.status}: {uk.common.all.toLowerCase()}
              </SelectItem>
              {REPORT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {REPORT_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Combobox
          className="w-40"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined, page: undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({ value: p.id, label: p.code }))}
          placeholder={uk.registry.project}
          allowClear
        />
        <Combobox
          className="w-44"
          value={search.center}
          onChange={(v) => setSearch({ center: v ?? undefined, page: undefined })}
          options={(registry.data?.centers ?? []).map((c) => ({ value: c.id, label: c.name }))}
          placeholder={uk.registry.center}
          allowClear
        />
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <span>{uk.contests.from}</span>
          <DateInput
            className="w-36"
            value={search.from}
            onChange={(v) => setSearch({ from: v ?? undefined, page: undefined })}
          />
          <span>{uk.contests.to}</span>
          <DateInput
            className="w-36"
            value={search.to}
            onChange={(v) => setSearch({ to: v ?? undefined, page: undefined })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={Boolean(search.violations)}
            onCheckedChange={(v) =>
              setSearch({ violations: v === true || undefined, page: undefined })
            }
          />
          {t.withViolations}
        </label>
        {approvable.length > 0 && (
          <Button
            className="ml-auto"
            disabled={chosen.length === 0 || approve.isPending}
            onClick={() => approve.mutate({ ids: chosen })}
          >
            <CheckIcon /> {t.approveSelected(chosen.length)}
          </Button>
        )}
        {approvable.length > 1 && (
          <Button variant="ghost" onClick={() => setSelected(new Set(approvable.map((r) => r.id)))}>
            {uk.common.all}
          </Button>
        )}
      </div>
      <DataTable
        columns={columns}
        data={list.data?.items}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={list.isPending}
        getRowId={(r) => r.id}
        onRowClick={(r) => setSearch({ report: r.id })}
        empty={
          <EmptyState
            icon={ClipboardCheckIcon}
            title={tab === 'awaiting' ? t.emptyAwaiting : t.empty}
          />
        }
      />
      <ReportSheet
        id={search.report ?? null}
        onOpenChange={(o) => !o && setSearch({ report: undefined })}
      />
    </>
  );
}
